import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { X, ArrowLeft, ArrowRight, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import { fetchProduct } from "../lib/products";
import { FadeUp } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import Footer from "../components/Footer";

function TiltImage({ children }) {
    const ref = React.useRef(null);
    const rxm = useMotionValue(0);
    const rym = useMotionValue(0);
    const rx = useSpring(rxm, { stiffness: 140, damping: 18 });
    const ry = useSpring(rym, { stiffness: 140, damping: 18 });
    return (
        <motion.div
            ref={ref}
            data-testid="tilt-view"
            style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
            onMouseMove={(e) => {
                const r = ref.current.getBoundingClientRect();
                rym.set(((e.clientX - r.left) / r.width - 0.5) * 10);
                rxm.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
            }}
            onMouseLeave={() => {
                rxm.set(0);
                rym.set(0);
            }}
            className="[transform-style:preserve-3d]"
        >
            {children}
        </motion.div>
    );
}

function Accordion({ title, children, testid }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-[rgba(10,10,10,0.12)]">
            <button
                data-testid={testid}
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between py-4 text-left"
            >
                <span className="font-display text-[10px] font-bold uppercase tracking-[0.3em] text-[#0A0A0A]">{title}</span>
                {open ? <Minus size={13} strokeWidth={1.5} /> : <Plus size={13} strokeWidth={1.5} />}
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="pb-5 text-xs leading-relaxed text-[#8E8D8A]">{children}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function ProductPage() {
    const { id } = useParams();
    const { addItem, products } = useStore();
    const [product, setProduct] = useState(null);
    const [size, setSize] = useState(null);
    const [color, setColor] = useState(null);
    const [printType, setPrintType] = useState("STANDARD");
    const [activeImg, setActiveImg] = useState(0);
    const [fullscreen, setFullscreen] = useState(false);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setProduct(null);
        setSize(null);
        setColor(null);
        setPrintType("STANDARD");
        setActiveImg(0);
        fetchProduct(id)
            .then((data) => {
                setProduct(data);
                setColor(data.colors[0]);
            })
            .catch(() => setNotFound(true));
    }, [id]);

    const selectedVariant = useMemo(() => {
        if (!product?.variants) return null;
        return product.variants.find((v) => v.size === size && v.color === color && (v.print || "STANDARD") === printType) || null;
    }, [product, size, color, printType]);

    const colorImage = useMemo(() => {
        if (!product?.variants || !color) return null;
        const exact = product.variants.find((v) => v.color === color && (v.print || "STANDARD") === printType && v.image);
        if (exact) return exact.image;
        const anyForColor = product.variants.find((v) => v.color === color && v.image);
        return anyForColor?.image || null;
    }, [product, color, printType]);

    const price = useMemo(() => {
        if (!product) return 0;
        if (selectedVariant) return selectedVariant.price;
        return printType === "RAISED" && product.hasRaised ? product.raised_price : product.price;
    }, [product, printType, selectedVariant]);

    const images = useMemo(() => {
        if (!product) return [];
        if (printType === "RAISED") return [product.raisedImage || colorImage || product.back, colorImage || product.front];
        return [colorImage || product.front, product.back];
    }, [product, printType, colorImage]);

    const gallery = useMemo(() => {
        const items = images.map((src) => ({ type: "image", src }));
        if (product?.video) items.push({ type: "video", src: product.video });
        return items;
    }, [images, product]);

    const related = useMemo(() => {
        if (!product) return [];
        return products.filter((p) => p.id !== product.id && p.collection === product.collection).slice(0, 4);
    }, [product, products]);

    const add = () => {
        if (!size) {
            toast.error("Select a size first");
            return;
        }
        if (printType === "RAISED" && product.hasRaised && !selectedVariant) {
            toast.error("Raised Reality is not available in this size / color");
            return;
        }
        addItem({
            product_id: product.id,
            name: product.name,
            size,
            color,
            print_type: printType,
            qty: 1,
            price,
            image: printType === "RAISED" && product.raisedImage ? product.raisedImage : product.front,
            variant_id: selectedVariant?.id,
        });
        toast.success("Added to bag");
    };

    useEffect(() => {
        const esc = (e) => e.key === "Escape" && setFullscreen(false);
        window.addEventListener("keydown", esc);
        return () => window.removeEventListener("keydown", esc);
    }, []);

    if (notFound) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-6">
                <p className="text-xs uppercase tracking-[0.3em] text-[#6B6A66]">Piece not found</p>
                <Link to="/shop" className="link-underline font-display text-[11px] font-bold uppercase tracking-[0.3em]">
                    Back to shop
                </Link>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <p className="text-[10px] uppercase tracking-[0.4em] text-[#6B6A66]">Loading</p>
            </div>
        );
    }

    return (
        <div data-testid="product-page" className="pt-28 lg:pt-32">
            <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-16">
                <Link
                    data-testid="back-to-shop"
                    to="/shop"
                    className="mb-8 inline-flex items-center gap-2 text-[9px] uppercase tracking-[0.3em] text-[#6B6A66] transition-colors hover:text-[#0A0A0A]"
                >
                    <ArrowLeft size={12} /> Shop
                </Link>
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                    <div className="space-y-4 lg:col-span-7">
                        {gallery.map((item, i) => {
                            if (item.type === "video") {
                                return (
                                    <motion.div
                                        key={`video-${i}`}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                        className="overflow-hidden bg-[#0A0A0A]"
                                    >
                                        <video
                                            data-testid="product-video"
                                            src={item.src}
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            className="aspect-[4/5] w-full object-cover"
                                        />
                                    </motion.div>
                                );
                            }
                            const src = item.src;
                            const frame = (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                    className="group overflow-hidden bg-[#ECEAE4]"
                                    data-cursor="VIEW"
                                    onClick={() => {
                                        setActiveImg(i);
                                        setFullscreen(true);
                                    }}
                                >
                                    <img
                                        src={src}
                                        alt={`${product.name} — view ${i + 1}`}
                                        loading={i === 0 ? "eager" : "lazy"}
                                        className="aspect-[4/5] w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03]"
                                    />
                                </motion.div>
                            );
                            return product.category === "Accessories" ? (
                                <TiltImage key={`${printType}-${i}`}>{frame}</TiltImage>
                            ) : (
                                React.cloneElement(frame, { key: `${printType}-${i}` })
                            );
                        })}
                        {product.category === "Accessories" && (
                            <p data-testid="tilt-hint" className="text-center text-[9px] uppercase tracking-[0.35em] text-[#6B6A66]">
                                Move across the image — 3D tilt view
                            </p>
                        )}
                    </div>

                    <div className="lg:col-span-5">
                        <div className="lg:sticky lg:top-28">
                            <FadeUp>
                                <p className="mb-3 text-[9px] uppercase tracking-[0.4em] text-[#6B6A66]">
                                    {product.collection} — {product.tag}
                                </p>
                                <h1 className="font-display text-2xl font-black uppercase tracking-tight text-[#0A0A0A] sm:text-4xl">
                                    {product.name}
                                </h1>
                                <p data-testid="product-price" className="mt-4 text-sm tracking-[0.15em] text-[#0A0A0A]">
                                    ${price} CAD
                                </p>
                                <p className="mt-6 max-w-md text-sm leading-relaxed text-[#8E8D8A]">{product.description}</p>
                            </FadeUp>

                            <div className="mt-10">
                                <p className="mb-4 text-[9px] uppercase tracking-[0.35em] text-[#6B6A66]">
                                    Color — <span className="text-[#0A0A0A]">{color}</span>
                                </p>
                                <div className="flex gap-3">
                                    {product.colors.map((c) => (
                                        <button
                                            key={c}
                                            data-testid={`color-option-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                            onClick={() => setColor(c)}
            className={`rounded-full border px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-all duration-300 ${
                                                color === c
                                                    ? "border-[#0A0A0A] text-[#0A0A0A] shadow-[0_8px_20px_-6px_rgba(10,10,10,0.3)]"
                                                    : "border-[rgba(10,10,10,0.15)] text-[#8E8D8A] hover:-translate-y-0.5 hover:border-[#8E8D8A]"
                                            }`}
                                        >
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-8">
                                <div className="mb-4 flex items-center justify-between">
                                    <p className="text-[9px] uppercase tracking-[0.35em] text-[#6B6A66]">Size</p>
                                    <button className="link-underline text-[9px] uppercase tracking-[0.25em] text-[#8E8D8A]">Size Guide</button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {product.sizes.map((s) => (
                                        <button
                                            key={s}
                                            data-testid={`size-option-${s.toLowerCase()}`}
                                            onClick={() => setSize(s)}
                                            className={`h-11 w-12 rounded-full border text-[10px] uppercase tracking-[0.15em] transition-all duration-300 ${
                                                size === s
                                                    ? "border-[#0A0A0A] bg-[#0A0A0A] text-[#F2F0EA] shadow-[0_10px_24px_-8px_rgba(10,10,10,0.4)]"
                                                    : "border-[rgba(10,10,10,0.15)] text-[#8E8D8A] hover:-translate-y-0.5 hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
                                            }`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {product.hasRaised && (
                            <div className="mt-8" data-testid="print-type-section">
                                <p className="mb-4 text-[9px] uppercase tracking-[0.35em] text-[#6B6A66]">Print Type</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        data-testid="print-type-standard"
                                        onClick={() => setPrintType("STANDARD")}
                                        className={`border px-4 py-4 text-left transition-colors duration-300 ${
                                            printType === "STANDARD"
                                                ? "border-[#0A0A0A]"
                                                : "border-[rgba(10,10,10,0.15)] hover:border-[#8E8D8A]"
                                        }`}
                                    >
                                        <p className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-[#0A0A0A]">Standard</p>
                                        <p className="mt-1 text-[9px] tracking-[0.15em] text-[#8E8D8A]">${product.price} CAD</p>
                                    </button>
                                    <button
                                        data-testid="print-type-raised"
                                        onClick={() => setPrintType("RAISED")}
                                        className={`border px-4 py-4 text-left transition-colors duration-300 ${
                                            printType === "RAISED"
                                                ? "border-[#C0C0C0]"
                                                : "border-[rgba(10,10,10,0.15)] hover:border-[#8E8D8A]"
                                        }`}
                                    >
                                        <p className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-[#0A0A0A]">
                                            Raised Reality
                                        </p>
                                        <p className="mt-1 text-[9px] tracking-[0.15em] text-[#8E8D8A]">
                                            +${product.raisedSurcharge} CAD
                                        </p>
                                    </button>
                                </div>
                            </div>
                            )}

                            <button
                                data-testid="add-to-bag-button"
                                onClick={add}
                                className="mt-10 w-full bg-[#0A0A0A] py-4 font-display text-[11px] font-bold uppercase tracking-[0.35em] text-[#F2F0EA] transition-colors hover:bg-white"
                            >
                                Add To Bag — ${price} CAD
                            </button>

                            <div className="mt-10">
                                <Accordion title="Details" testid="accordion-details">
                                    <ul className="space-y-2">
                                        {product.details.map((d) => (
                                            <li key={d}>— {d}</li>
                                        ))}
                                    </ul>
                                </Accordion>
                                <Accordion title="Material" testid="accordion-material">
                                    {product.material}
                                </Accordion>
                                <Accordion title="Fit" testid="accordion-fit">
                                    {product.fit}
                                </Accordion>
                                <Accordion title="Care" testid="accordion-care">
                                    {product.care}
                                </Accordion>
                                <Accordion title="Shipping & Returns" testid="accordion-shipping">
                                    Complimentary worldwide express shipping on orders over $300 CAD. Returns accepted within 14
                                    days, unworn with tags attached.
                                </Accordion>
                            </div>
                        </div>
                    </div>
                </div>

                {related.length > 0 && (
                    <section data-testid="related-products" className="py-24 lg:py-36">
                        <p className="mb-3 text-[9px] uppercase tracking-[0.4em] text-[#6B6A66]">Continue</p>
                        <h2 className="font-display mb-12 text-2xl font-extrabold uppercase tracking-tight text-[#0A0A0A] sm:text-4xl">
                            You May Also Like
                        </h2>
                        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-8">
                            {related.map((p) => (
                                <ProductCard key={p.id} product={p} />
                            ))}
                        </div>
                    </section>
                )}
            </div>

            <AnimatePresence>
                {fullscreen && (
                    <motion.div
                        data-testid="fullscreen-gallery"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-[190] flex items-center justify-center bg-[rgba(10,10,10,0.97)]"
                        onClick={() => setFullscreen(false)}
                    >
                        <button
                            data-testid="fullscreen-close"
                            className="absolute right-6 top-6 text-[#8E8D8A] hover:text-[#0A0A0A]"
                            aria-label="Close gallery"
                        >
                            <X size={22} strokeWidth={1.5} />
                        </button>
                        <button
                            data-testid="fullscreen-prev"
                            onClick={(e) => {
                                e.stopPropagation();
                                setActiveImg((v) => (v - 1 + images.length) % images.length);
                            }}
                            className="absolute left-6 text-[#8E8D8A] hover:text-[#0A0A0A]"
                            aria-label="Previous image"
                        >
                            <ArrowLeft size={22} strokeWidth={1.5} />
                        </button>
                        <motion.img
                            key={activeImg}
                            src={images[activeImg]}
                            alt={`${product.name} fullscreen`}
                            initial={{ opacity: 0, scale: 0.97 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            className="max-h-[85vh] max-w-[85vw] object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <button
                            data-testid="fullscreen-next"
                            onClick={(e) => {
                                e.stopPropagation();
                                setActiveImg((v) => (v + 1) % images.length);
                            }}
                            className="absolute right-6 text-[#8E8D8A] hover:text-[#0A0A0A]"
                            aria-label="Next image"
                        >
                            <ArrowRight size={22} strokeWidth={1.5} />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
            <Footer />
        </div>
    );
}
